import { useTranslation } from '@/i18n';
import {
  ActionMenu,
  ActionMenuItems,
  ButtonV2Props,
  IconTrailing,
  LinkButton,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useMemo } from 'react';
import {
  AgentEntryOpenDetail,
  useOpenAgentEntryModal,
} from '../../hooks/useOpenAgentEntryModal';
import styles from '../InvoiceReportsButtonGroup.module.scss';

const cx = classnames.bind(styles);

export { AgentEntryOpenDetail };

export type ProxyOnButtonGroupProps = {
  /** Report type id for "Create new" redirect (e.g. payment request 支払依頼). */
  reportTypeId?: string;
};

export const ProxyOnButtonGroup: FC<ProxyOnButtonGroupProps> = memo(
  ({ reportTypeId }) => {
    const { t } = useTranslation();
    const openAgentEntryModal = useOpenAgentEntryModal();

    const onAgentEntryRegister = useCallback(() => {
      openAgentEntryModal(AgentEntryOpenDetail.InvoiceReport);
    }, [openAgentEntryModal]);

    const onAgentEntryBulkUpload = useCallback(() => {
      openAgentEntryModal(AgentEntryOpenDetail.InvoiceReportBulkUpload);
    }, [openAgentEntryModal]);

    const handleAgentEntryKeyDown = useCallback(
      (fn: () => void) => (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fn();
        }
      },
      []
    );

    /** Agent-entry modal items: open 代理申請 modal via ap_web hidden link click. */
    const aiOcrBulkItems: ActionMenuItems = useMemo(
      () => [
        {
          key: 'ai_ocr_bulk',
          label: (
            <LinkButton
              isTransparent
              href='/invoice_upload/new'
              buttonContainerClassName={cx('btnLinkContainerCustom')}
              buttonClassName={cx(
                !reportTypeId ? 'btnLinkCustom_disabled' : 'btnLinkCustom'
              )}
            >
              {t('invoice_reports.button_groups.register_my_application')}
            </LinkButton>
          ),
          disabled: !reportTypeId,
        },

        {
          key: 'proxy_application',
          label: t('invoice_reports.button_groups.proxy_application'),
          onClick: onAgentEntryBulkUpload,
          onKeyDown: handleAgentEntryKeyDown(onAgentEntryBulkUpload),
        },
      ],
      [reportTypeId, t, onAgentEntryBulkUpload, handleAgentEntryKeyDown]
    );

    const createNewItems: ActionMenuItems = useMemo(
      () => [
        {
          key: 'create_new',
          label: (
            <LinkButton
              isTransparent
              href={
                reportTypeId
                  ? `/report_types/${reportTypeId}/invoice_reports/new`
                  : undefined
              }
              buttonContainerClassName={cx('btnLinkContainerCustom')}
              buttonClassName={cx(
                !reportTypeId ? 'btnLinkCustom_disabled' : 'btnLinkCustom'
              )}
            >
              {t('invoice_reports.button_groups.register_my_application')}
            </LinkButton>
          ),
          disabled: !reportTypeId,
        },
        {
          key: 'register_my_application',
          className: cx('btnLinkContainerCustom'),
          label: t('invoice_reports.button_groups.proxy_application'),
          onClick: onAgentEntryRegister,
          onKeyDown: handleAgentEntryKeyDown(onAgentEntryRegister),
        },
      ],
      [reportTypeId, t, onAgentEntryRegister, handleAgentEntryKeyDown]
    );

    const aiOcrButtonProps: ButtonV2Props = useMemo(
      () => ({
        children: t('invoice_reports.button_groups.ai_ocr_bulk_draft'),
        size: 'sm',
        isTertiary: false,
        rightIcon: <IconTrailing size={16} />,
      }),
      [t]
    );

    const createNewButtonProps: ButtonV2Props = useMemo(
      () => ({
        children: t('invoice_reports.button_groups.create_new'),
        size: 'sm',
        color: 'primary',
        isSecondary: false,
        isTertiary: false,
        rightIcon: <IconTrailing size={16} />,
      }),
      [t]
    );

    return (
      <>
        <ActionMenu
          type='button'
          items={aiOcrBulkItems}
          buttonProps={aiOcrButtonProps}
        />
        <ActionMenu
          type='button'
          items={createNewItems}
          buttonProps={createNewButtonProps}
          className={cx('actionMenu')}
        />
      </>
    );
  }
);

ProxyOnButtonGroup.displayName = 'ProxyOnButtonGroup';
